import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Send, Bot, Sparkles } from "lucide-react";
import { api } from "../api";
import { PageHeader } from "../components/ui";

const SUGGESTIONS = [
  "How do I build business credit?",
  "Am I funding ready?",
  "How do Net-30 accounts work?",
  "What should I complete next?",
  "How do I lower my utilization?",
  "Which documents am I missing?",
];

export default function Coach() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sessionId, setSessionId] = useState(null);
  const [loading, setLoading] = useState(false);
  const endRef = useRef(null);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, loading]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || loading) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: msg }]);
    setLoading(true);
    try {
      const { data } = await api.post("/coach/chat", { message: msg, session_id: sessionId });
      setSessionId(data.session_id);
      setMessages((m) => [...m, { role: "assistant", content: data.reply }]);
    } catch (e) {
      setMessages((m) => [...m, { role: "assistant", content: "Sorry, I couldn't respond right now. Please try again." }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col h-[calc(100vh-9rem)]">
      <PageHeader label="AI Assistant" title="AI Credit Coach"
        subtitle="Ask anything about business or personal credit. Educational guidance, personalized to your profile." />

      <div className="flex-1 cc-card rounded-2xl flex flex-col overflow-hidden">
        <div className="flex-1 overflow-y-auto cc-scroll p-5 sm:p-6 space-y-4">
          {messages.length === 0 && (
            <div className="h-full flex flex-col items-center justify-center text-center">
              <div className="flex h-14 w-14 items-center justify-center rounded-2xl cc-gradient cc-glow-blue mb-4">
                <Bot size={26} className="text-white" />
              </div>
              <h3 className="cc-heading text-lg font-500 text-white">How can I help you build credit?</h3>
              <p className="text-slate-400 text-sm mt-1.5 max-w-sm">Try one of these questions to get started.</p>
              <div className="mt-6 flex flex-wrap justify-center gap-2 max-w-lg">
                {SUGGESTIONS.map((s) => (
                  <button key={s} onClick={() => send(s)} data-testid="coach-suggestion"
                    className="rounded-full border border-white/10 cc-surface px-3.5 py-2 text-xs text-slate-300 cc-nav-item">
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}

          {messages.map((m, i) => (
            <motion.div key={m.id || `${m.role}-${i}-${(m.content || "").slice(0, 24)}`} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
              className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
              {m.role === "assistant" && (
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg cc-surface-2 text-blue-300 mr-2.5 mt-0.5">
                  <Sparkles size={15} />
                </div>
              )}
              <div className={`max-w-[80%] rounded-2xl px-4 py-3 text-sm leading-relaxed whitespace-pre-wrap ${
                m.role === "user" ? "cc-gradient text-white" : "cc-surface border border-white/[0.06] text-slate-200"}`}
                data-testid={`coach-msg-${m.role}`}>
                {m.content}
              </div>
            </motion.div>
          ))}

          {loading && (
            <div className="flex justify-start">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg cc-surface-2 text-blue-300 mr-2.5">
                <Sparkles size={15} />
              </div>
              <div className="cc-surface border border-white/[0.06] rounded-2xl px-4 py-3.5 flex gap-1.5">
                {[0, 1, 2].map((i) => (
                  <motion.span key={i} className="h-2 w-2 rounded-full bg-blue-400/70"
                    animate={{ opacity: [0.3, 1, 0.3] }} transition={{ duration: 1.2, repeat: Infinity, delay: i * 0.2 }} />
                ))}
              </div>
            </div>
          )}
          <div ref={endRef} />
        </div>

        <form onSubmit={(e) => { e.preventDefault(); send(); }}
          className="border-t border-white/[0.06] p-3 sm:p-4 flex items-center gap-2.5">
          <input value={input} onChange={(e) => setInput(e.target.value)} placeholder="Ask your credit coach…"
            data-testid="coach-input" className="cc-input flex-1 rounded-xl px-4 py-3 text-sm" />
          <button type="submit" disabled={loading || !input.trim()} data-testid="coach-send"
            className="cc-btn-primary flex h-11 w-11 items-center justify-center rounded-xl disabled:opacity-50">
            <Send size={17} />
          </button>
        </form>
      </div>
    </div>
  );
}
